import React, { useState, useEffect, useMemo } from 'react';
import { Paper, Badge, Select, RingProgress, Loader } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import styles from './InitiativeSummary.module.scss';
import { QuickInsightCard, insightCardStyles } from '../OrganisationalObjectives';
import { Icon, type IconName } from '../../../assets/icons/Icon';
import { useApiClient } from '../../../api/ApiClientContext';
import { StreamDetailsModal } from '../../initiativeInsights/StreamDetailsModal';
import type { KPIInsightsResponse, StreamSummaryRow, InitiativeInsightsResponse } from '../../../api/Api';


// API response types for progress endpoint (BDD-calculated)
interface GoalProgressApiResponse {
  id: string;
  title: string;
  kpis_on_target: number;
  total_kpis: number;
  progress_percent: number | null;
  programme_id: string | null;
}

interface ObjectiveProgressApiResponse {
  id: string;
  title: string;
  kpis_on_target: number;
  total_kpis: number;
  progress_percent: number | null;
  goals: GoalProgressApiResponse[];
}

interface AllObjectivesProgressApiResponse {
  objectives: ObjectiveProgressApiResponse[];
}

// Goal data structure for the component
interface GoalDataType {
  id: string;
  title: string;
  progress: number;
  target: number;
  status: 'AT RISK' | 'BEHIND TARGET' | 'ON TRACK';
  statusColor: string;
  ringColor: string;
  kpis: { name: string; current: string; target: string }[];
}

// Format KPI value with unit
const formatKpiValue = (value: number | null, unit: string, precision: number = 0): string => {
  if (value === null || value === undefined) return '-';
  const unitLower = unit?.toLowerCase() || '';
  if (unitLower === '%' || unitLower === 'percent' || unitLower === 'percentage') {
    const pctVal = value * 100;
    const pctFormatted = precision > 0 ? pctVal.toFixed(precision) : Math.round(pctVal).toString();
    return `${pctFormatted}%`;
  }
  const formatted = precision > 0 ? value.toFixed(precision) : Math.round(value).toString();
  if (unitLower === '£' || unitLower === 'gbp') return `£${formatted}`;
  if (unitLower === '$' || unitLower === 'usd') return `$${formatted}`;
  if (unitLower === 'millions' || unitLower === 'million') return `${formatted}M`;
  if (unitLower === 'billions' || unitLower === 'billion') return `${formatted}B`;
  if (unitLower === 'integer' || unitLower === 'count' || unitLower === 'decimal') return formatted;
  return `${formatted}${unit ? ` ${unit}` : ''}`;
};

// Get latest actual value from measurements (sort by week_number, not array index)
const getLatestActual = (actuals: any[]): number | null => {
  if (!actuals || actuals.length === 0) return null;
  const sorted = [...actuals].sort((a, b) => b.week_number - a.week_number);
  return sorted[0].actual_value;
};

const getGoalStatus = (progressPercent: number | null): { status: 'AT RISK' | 'BEHIND TARGET' | 'ON TRACK'; statusColor: string; ringColor: string } => {
  if (progressPercent === null || progressPercent < 50) {
    return { status: 'AT RISK', statusColor: 'red', ringColor: '#FA5252' };
  }
  if (progressPercent >= 80) {
    return { status: 'ON TRACK', statusColor: 'green', ringColor: '#40C057' };
  }
  return { status: 'BEHIND TARGET', statusColor: 'orange', ringColor: '#FD7E14' };
};

// Filter goals from progress data by programme_id
const filterGoalsByProgramme = (
  progressData: AllObjectivesProgressApiResponse | undefined,
  programmeId: string | null
): GoalProgressApiResponse[] => {
  if (!progressData?.objectives || !programmeId) return [];

  const filteredGoals: GoalProgressApiResponse[] = [];
  for (const obj of progressData.objectives) {
    for (const goal of obj.goals) {
      if (goal.programme_id === programmeId) {
        filteredGoals.push(goal);
      }
    }
  }
  return filteredGoals;
};

// Transform goals API response to display format, using progress data for BDD-calculated progress
const transformGoalsToDisplay = (
  goalsApiData: any[] | undefined,
  progressByGoalId: Map<string, GoalProgressApiResponse>
): GoalDataType[] => {
  if (!goalsApiData) return [];

  return goalsApiData.map((goal: any) => {
    const kpis = goal.kpis || [];
    // Use BDD-calculated progress from progress endpoint if available
    const progressData = progressByGoalId.get(goal.id);
    const progressPercent = progressData?.progress_percent ?? null;
    const { status, statusColor, ringColor } = getGoalStatus(progressPercent);

    return {
      id: goal.id,
      title: goal.title,
      progress: Math.round(progressPercent ?? 0),
      target: 100,
      status,
      statusColor,
      ringColor,
      kpis: kpis.slice(0, 2).map((kpi: any) => ({
        name: kpi.title?.toUpperCase() || 'KPI',
        current: formatKpiValue(getLatestActual(kpi.actuals) ?? kpi.baseline, kpi.unit, kpi.precision),
        target: formatKpiValue(kpi.target, kpi.unit, kpi.precision),
      })),
    };
  });
};

const getStreamStatus = (activeUsersPct: number): { status: string; statusColor: string; ringColor: string } => {
  if (activeUsersPct >= 80) {
    return { status: 'EXCEEDING GOALS', statusColor: 'teal', ringColor: '#12B886' };
  }
  if (activeUsersPct >= 50) {
    return { status: 'ON TRACK', statusColor: 'indigo', ringColor: '#4C6EF5' };
  }
  return { status: 'NEEDS ATTENTION', statusColor: 'red', ringColor: '#FA5252' };
};

const getGoalArrowIcon = (status: string): IconName => {
  if (status === 'ON TRACK') return 'BlackArrowUp';
  if (status === 'BEHIND TARGET') return 'BlackArrowRight';
  return 'BlackArrowDown';
};

const getStreamArrowIcon = (status: string): IconName => {
  if (status === 'EXCEEDING GOALS') return 'BlackArrowUp';
  if (status === 'ON TRACK') return 'BlackArrowRight';
  return 'BlackArrowDown';
};

interface GoalCardProps {
  goal: GoalDataType;
}

const GoalCard: React.FC<GoalCardProps> = ({ goal }) => (
  <Paper className={styles.goalCard} radius="md" withBorder>
    <div className={styles.goalCard__header}>
      <h3 className={styles.goalCard__title}>{goal.title}</h3>
      <div className={styles.goalCard__progress}>
        <RingProgress
          className={styles.ringProgress}
          size={60}
          thickness={5}
          roundCaps
          rootColor="#E9ECEF"
          sections={[{ value: goal.progress, color: goal.ringColor }]}
          styles={{ root: { transform: 'scale(1.22)', transformOrigin: 'center' } }}
          label={
            <div className={styles.goalCard__ringIcon}>
              <Icon iconName={getGoalArrowIcon(goal.status)} width="1rem" height="1rem" />
            </div>
          }
        />
        <div>
          <div className={styles.goalCard__progressLabel}>
            <span className={styles.goalCard__progressValue}>{goal.progress}%</span>
            <span className={styles.goalCard__progressTarget}> / {goal.target}%</span>
          </div>
          <Badge
            size="xs"
            variant="light"
            color={goal.statusColor}
            className={styles.goalCard__statusBadge}
          >
            {goal.status}
          </Badge>
        </div>
      </div>
    </div>

    <div className={styles.goalCard__kpisSection}>
      <div className={styles.goalCard__kpisLabel}>KPIs</div>
      <div className={styles.goalCard__kpisGrid}>
        {goal.kpis.map((kpi, index) => (
          <Paper key={index} className={styles.goalCard__kpiCard} radius="md" withBorder>
            <div className={styles.goalCard__kpiHeader}>
              <span className={styles.goalCard__kpiName}>{kpi.name}</span>
              <Icon iconName="GreyChartIcon" width="1.25rem" height="1.25rem" />
            </div>
            <div className={styles.goalCard__kpiValues}>
              <span className={styles.goalCard__kpiCurrent}>{kpi.current}</span>
              <span className={styles.goalCard__kpiSeparator}> / </span>
              <span className={styles.goalCard__kpiTarget}>{kpi.target}</span>
            </div>
          </Paper>
        ))}
      </div>
    </div>
  </Paper>
);

interface StreamCardProps {
  stream: StreamSummaryRow;
  onViewDetails: () => void;
}

const StreamCard: React.FC<StreamCardProps> = ({ stream, onViewDetails }) => {
  const { status, statusColor, ringColor } = getStreamStatus(stream.active_users_pct);

  return (
    <Paper className={styles.streamCard} radius="md" withBorder>
      <div className={styles.streamCard__header}>
        <h3 className={styles.streamCard__name}>{stream.name}</h3>
      </div>

      <div className={styles.streamCard__progressSection}>
        <RingProgress
          className={styles.ringProgress}
          size={60}
          thickness={5}
          roundCaps
          rootColor="#E9ECEF"
          sections={[{ value: stream.active_users_pct, color: ringColor }]}
          styles={{ root: { transform: 'scale(1.22)', transformOrigin: 'center' } }}
          label={
            <span className={styles.streamCard__ringLabel}>
              <Icon iconName={getStreamArrowIcon(status)} width="1rem" height="1rem" />
            </span>
          }
        />
        <div className={styles.streamCard__activeInfo}>
          <span className={styles.streamCard__activeLabel}>Active users: {stream.active_users_pct}%</span>
          <Badge
            size="xs"
            variant="light"
            color={statusColor}
            className={styles.streamCard__statusBadge}
          >
            {status}
          </Badge>
        </div>
      </div>

      <div className={styles.streamCard__stats}>
        <div className={styles.streamCard__stat}>
          <span className={styles.streamCard__statLabel}>Active cohorts</span>
          <span className={styles.streamCard__statValue}>{stream.cohorts_count}</span>
        </div>
        <div className={styles.streamCard__stat}>
          <span className={styles.streamCard__statLabel}>Total users</span>
          <span className={styles.streamCard__statValue}>{stream.total_users}</span>
        </div>
        <div className={styles.streamCard__stat}>
          <span className={styles.streamCard__statLabel}>Avg completion</span>
          <span className={styles.streamCard__statValue}>{stream.avg_completion != null ? `${Math.round(stream.avg_completion)}%` : '-'}</span>
        </div>
        <div className={styles.streamCard__stat}>
          <span className={styles.streamCard__statLabel}>Behaviours</span>
          <span className={styles.streamCard__statValue}>{stream.behaviours_count}</span>
        </div>
      </div>

      <button className={styles.streamCard__viewDetails} onClick={onViewDetails}>View details</button>
    </Paper>
  );
};

interface InitiativeSummaryProps {
  className?: string;
}

export const InitiativeSummary: React.FC<InitiativeSummaryProps> = ({ className = '' }) => {
  const [selectedInitiative, setSelectedInitiative] = useState<string | null>(null);
  const [selectedStreamId, setSelectedStreamId] = useState<string | null>(null);
  const apiClient = useApiClient();

  const { data: programmes = [], isLoading: programmesLoading } = useQuery({
    queryKey: ['programmes'],
    queryFn: async () => {
      const response = await apiClient.http.request<any[], any>({
        path: '/api/v1/programmes/',
        method: 'GET',
        secure: true,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const initiativesOptions = (programmes || []).map((p: any) => ({
    value: p.id,
    label: p.name,
  }));

  useEffect(() => {
    if (programmes && programmes.length > 0 && !selectedInitiative) {
      setSelectedInitiative(programmes[0].id);
    }
  }, [programmes, selectedInitiative]);

  const { data: progressData, isLoading: progressLoading } = useQuery({
    queryKey: ['kpi-objectives-progress'],
    queryFn: async () => {
      const response = await apiClient.http.request<AllObjectivesProgressApiResponse, any>({
        path: '/api/v1/kpi/objectives/progress',
        method: 'GET',
        secure: true,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: insightsData } = useQuery<KPIInsightsResponse>({
    queryKey: ['kpi-insights-initiative', selectedInitiative],
    queryFn: async () => {
      const response = await apiClient.http.request<KPIInsightsResponse, any>({
        path: `/api/v1/kpi/insights?programme_id=${selectedInitiative}`,
        method: 'GET',
        secure: true,
      });
      return response.data;
    },
    enabled: !!selectedInitiative,
    staleTime: 5 * 60 * 1000,
  });

  const { data: programmeGoals, isLoading: goalsLoading, error } = useQuery({
    queryKey: ['programme-goals', selectedInitiative],
    queryFn: async () => {
      const response = await apiClient.http.request<any[], any>({
        path: `/api/v1/kpi/programmes/${selectedInitiative}/goals`,
        method: 'GET',
        secure: true,
      });
      return response.data;
    },
    enabled: !!selectedInitiative,
    staleTime: 5 * 60 * 1000,
  });

  const { data: initiativeData } = useQuery<InitiativeInsightsResponse>({
    queryKey: ['initiative-streams', selectedInitiative],
    queryFn: async () => {
      const response = await apiClient.http.request<InitiativeInsightsResponse, any>({
        path: `/api/v1/insights/initiative/${selectedInitiative}`,
        method: 'GET',
        secure: true,
      });
      return response.data;
    },
    enabled: !!selectedInitiative,
    staleTime: 5 * 60 * 1000,
  });

  const streams = initiativeData?.streams ?? [];

  const filteredProgressGoals = useMemo(
    () => filterGoalsByProgramme(progressData, selectedInitiative),
    [progressData, selectedInitiative]
  );

  const progressByGoalId = useMemo(() => {
    const map = new Map<string, GoalProgressApiResponse>();
    for (const goal of filteredProgressGoals) {
      map.set(goal.id, goal);
    }
    return map;
  }, [filteredProgressGoals]);

  const goals = useMemo(
    () => transformGoalsToDisplay(programmeGoals, progressByGoalId),
    [programmeGoals, progressByGoalId]
  );

  const isInitialLoading = programmesLoading || progressLoading;

  if (isInitialLoading) {
    return (
      <div className={`${styles.initiativeSummary} ${className}`} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    console.error('Error fetching KPI data:', error);
  }

  const kpisOnTarget = insightsData?.kpis_on_target ?? 0;
  const totalKpis = insightsData?.total_kpis ?? 0;
  const changePercent = insightsData?.change_percent ?? null;
  const trend = (insightsData?.trend as 'up' | 'down' | 'flat') ?? null;
  const mostImproved = insightsData?.most_improved_kpi ?? null;
  const habitsLogged = insightsData?.habits_logged ?? null;

  return (
    <div className={`${styles.initiativeSummary} ${className}`}>

      <div className={styles.initiativeSelector}>
        <h3 className={styles.initiativeSelector__title}>Initiative summary</h3>
        <Select
          value={selectedInitiative}
          onChange={setSelectedInitiative}
          data={initiativesOptions}
          placeholder="Select initiative"
          className={styles.initiativeSelector__select}
          rightSection={
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
        />
      </div>

      {/* Quick Insights */}
      <div className={styles.quickInsights}>
        <div className={styles.quickInsights__cards}>
          <QuickInsightCard title="KPIS ON TARGET">
            <div className={insightCardStyles.insightCard__row}>
              <span className={insightCardStyles.insightCard__mainValue}>
                {kpisOnTarget} / {totalKpis}
              </span>
              {changePercent !== null && (
                <div className={`${insightCardStyles.insightCard__change} ${trend === 'down' ? insightCardStyles['insightCard__change--down'] : insightCardStyles['insightCard__change--up']}`}>
                  {changePercent}% <Icon iconName={trend === 'down' ? 'ArrowDownIcon' : 'GreenUpIcon'} width="0.5rem" height="0.5rem" />
                </div>
              )}
            </div>
            <span className={insightCardStyles.insightCard__subtitle}>Compared to previous week</span>
          </QuickInsightCard>

          <QuickInsightCard title="MOST IMPROVED KPI">
            {mostImproved ? (
              <>
                <span className={insightCardStyles.insightCard__kpiName}>{mostImproved.name}</span>
                <div className={insightCardStyles.insightCard__row}>
                  <span className={insightCardStyles.insightCard__mainValue}>
                    {Math.round(mostImproved.current_value * 100)}%
                  </span>
                  <div className={`${insightCardStyles.insightCard__change} ${mostImproved.trend === 'down' ? insightCardStyles['insightCard__change--down'] : insightCardStyles['insightCard__change--up']}`}>
                    {mostImproved.improvement_percent}% <Icon iconName={mostImproved.trend === 'down' ? 'ArrowDownIcon' : 'GreenUpIcon'} width="0.5rem" height="0.5rem" />
                  </div>
                </div>
                <span className={insightCardStyles.insightCard__subtitle}>Compared to previous week</span>
              </>
            ) : (
              <span className={insightCardStyles.insightCard__mainValue}>-</span>
            )}
          </QuickInsightCard>

          <QuickInsightCard title="HABITS LOGGED THIS WEEK">
            <div className={insightCardStyles.insightCard__row}>
              <span className={insightCardStyles.insightCard__mainValue}>
                {habitsLogged ? habitsLogged.count.toLocaleString() : '0'}
              </span>
              {habitsLogged && (
                <div className={`${insightCardStyles.insightCard__change} ${habitsLogged.trend === 'down' ? insightCardStyles['insightCard__change--down'] : insightCardStyles['insightCard__change--up']}`}>
                  {habitsLogged.change_percent}% <Icon iconName={habitsLogged.trend === 'down' ? 'ArrowDownIcon' : 'GreenUpIcon'} width="0.5rem" height="0.5rem" />
                </div>
              )}
            </div>
            <span className={insightCardStyles.insightCard__subtitle}>
              {habitsLogged ? `Avg: ${Math.round(habitsLogged.avg_per_user)} per user` : ''}
            </span>
          </QuickInsightCard>
        </div>
      </div>

      {/* Goals Section */}
      <div className={styles.goals}>
        <h2 className={styles.goals__title}>Goals</h2>
        {goalsLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Loader size="md" />
          </div>
        ) : goals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-gray-6)' }}>
            No goals assigned to this initiative
          </div>
        ) : (
          <div className={styles.goals__grid}>
            {goals.map((goal) => (
              <GoalCard key={goal.id} goal={goal} />
            ))}
          </div>
        )}
      </div>

      {/* Streams Overview Section */}
      {streams.length > 0 && (
        <div className={styles.streams}>
          <h2 className={styles.streams__title}>Streams overview</h2>
          <div className={styles.streams__list}>
            {streams.map((stream) => (
              <StreamCard
                key={stream.id}
                stream={stream}
                onViewDetails={() => setSelectedStreamId(stream.id)}
              />
            ))}
          </div>
        </div>
      )}

      {selectedStreamId && selectedInitiative && (
        <StreamDetailsModal
          programmeId={selectedInitiative}
          streamId={selectedStreamId}
          breadcrumbLabel="Initiative summary"
          onClose={() => setSelectedStreamId(null)}
        />
      )}
    </div>
  );
};

export default InitiativeSummary;

